// Rendimento por setor — a REGRA, isolada de banco e de Drizzle.
//
// Lê linhas de `venda_colheita_rateio` (o FATO materializado pela Fase 1) e devolve o
// kg/cacho no grão em que decisão de campo acontece: setor × mês × variedade.
//
// ── POR QUE O GRÃO DE GRUPO É SEGURO E O DE VENDA NÃO ──────────────────────────────────
// O kg/cacho POR VENDA é distorcido quando as cestas são aninhadas: a venda 20 aparece com
// 45,9 kg/cacho de Prata porque todo o seu peso cai num grupo só, enquanto a 24, que cobre
// cinco grupos incluindo aquele, aparece com 17,06. É propriedade da CESTA, não do setor.
//
// No grão de GRUPO (dia × setor × variedade) isso cancela, porque a alocação CONSERVA
// (Σ_s aloc(s,g) = C(g), ver shared/rateio-colheita.ts). Medido na produção: 81 grupos de
// junho a agosto, ZERO violam a conservação. B2 / 27-07 / Prata: 151 cachos, 1.801 kg
// (venda 20) + 1.906 kg (venda 24) = 3.707 kg → 24,55 kg/cacho. Esse é o rendimento do
// setor naquele dia; o 45,9 é propriedade da venda.
//
// ⚠️ A REGRA QUE NÃO PODE SER VIOLADA: a célula soma Σ kg e Σ cachos e divide NO FIM.
// NUNCA média dos `kgPorCacho` das vendas. A média simples de 45,9 e 17,06 dá 31,48 — 28%
// acima do valor real. É a mesma regra que já quebrou duas vezes nesta feature (o divisor
// 1.198 do rateio antigo e o card Colhido de julho): numerador e denominador do MESMO
// conjunto.
//
// ⚠️ Typecheck do CI roda em ES3: nada de spread sobre Set/Map (`[...new Set()]` → TS2802).

// ─── Constantes de calibração ───────────────────────────────────────────────────────────

/** Banda fixa e chumbada que existia antes desta fase (server/storage/vendas.ts). Continua
 *  como FALLBACK: variedade sem histórico suficiente não ganha banda aprendida. */
export const BANDA_FIXA = { min: 5, max: 40 };

/**
 * Mínimo de grupos para uma variedade ter banda APRENDIDA.
 *
 * Guard-rail deliberado: com 19 grupos a Nanica cai na banda fixa já no primeiro deploy,
 * que é a melhor forma de o fallback não estar quebrado no dia em que precisar. É uma
 * escolha, não uma verdade — revisitar quando houver mais histórico.
 */
export const N_MINIMO_BANDA = 20;

/**
 * Abaixo disto a célula ganha o selo "amostra pequena".
 *
 * ~metade da mediana de cachos por célula na amostra atual (jun–ago/2026). É uma ESCOLHA,
 * não uma verdade: A1 em agosto sobe +46% sobre 60 cachos num único dia e B2 sobe 40% sobre
 * 89 — sem o selo, a tela vira uma máquina de conclusão apressada sobre adubação de setor,
 * que é exatamente o tipo de decisão cara que ela existe para informar. Revisitar quando
 * houver mais histórico.
 */
export const CACHOS_AMOSTRA_PEQUENA = 80;

// ─── Tipos ──────────────────────────────────────────────────────────────────────────────

/** Uma linha de `venda_colheita_rateio` já com os nomes resolvidos. `data` é a data da
 *  COLHEITA (nunca a da venda) — as duas grandezas têm datas diferentes, e trocá-las é a
 *  armadilha do #149. */
export interface LinhaRendimento {
  vendaId: string;
  data: string; // YYYY-MM-DD, data de colheita
  setorId: string;
  setorNome: string;
  variedadeId: string | null;
  variedadeNome: string | null;
  cachosAlocados: number;
  kgAlocados: number;
}

/** Grupo = (dia × setor × variedade). É a unidade da distribuição aprendida (§4). */
export interface GrupoRendimento {
  data: string;
  mes: string; // YYYY-MM
  setorId: string;
  variedadeId: string;
  kg: number;
  cachos: number;
  kgPorCacho: number;
}

/** Célula = (setor × mês × variedade). É a unidade da tela. */
export interface CelulaRendimento {
  setorId: string;
  setorNome: string;
  mes: string; // YYYY-MM
  variedadeId: string;
  variedadeNome: string;
  kg: number;
  cachos: number;
  /** `null` — nunca `Infinity` — quando não há cacho alocado. Sem denominador não há
   *  indicador, e zero afirmaria algo que ninguém mediu. */
  kgPorCacho: number | null;
  /** Dias distintos de colheita que compõem a célula. */
  dias: number;
  /** Vendas distintas que contribuíram para a célula. */
  vendas: number;
  /** Ver `marcarIndistintas`. */
  indistinto: boolean;
  /** `cachos < CACHOS_AMOSTRA_PEQUENA`. */
  amostraPequena: boolean;
}

export interface DistribuicaoVariedade {
  variedadeId: string;
  variedadeNome: string;
  n: number;
  min: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  max: number;
  /** `false` quando `n < N_MINIMO_BANDA` — a variedade cai na banda fixa e a tela DIZ isso. */
  aprendida: boolean;
}

export interface Banda {
  min: number;
  max: number;
  aprendida: boolean;
}

// ─── Chaves ─────────────────────────────────────────────────────────────────────────────

export function mesDe(data: string): string {
  return data.slice(0, 7);
}

export function chaveGrupoRendimento(data: string, setorId: string, variedadeId: string): string {
  return `${data}|${setorId}|${variedadeId}`;
}

export function chaveCelula(setorId: string, mes: string, variedadeId: string): string {
  return `${setorId}|${mes}|${variedadeId}`;
}

/** Linha que entra em indicador: alocou cacho E tem variedade. Linha inócua (aloc = 0) é
 *  associação marcada no setor errado — ela precisa aparecer no DETALHE da venda, mas não
 *  pode entrar num agregado de rendimento, onde só somaria ruído a um denominador. */
function contribui(l: LinhaRendimento): boolean {
  return l.cachosAlocados > 0 && l.variedadeId !== null;
}

// ─── Agregação ──────────────────────────────────────────────────────────────────────────

/**
 * Grupos (dia × setor × variedade) — Σ kg e Σ cachos das vendas que tocam o grupo.
 *
 * Por conservação, `cachos` aqui é C(g) sempre que alguma venda cobre o grupo inteiro.
 */
export function agregarGrupos(linhas: LinhaRendimento[]): GrupoRendimento[] {
  const mapa = new Map<string, GrupoRendimento>();
  for (const l of linhas) {
    if (!contribui(l)) continue;
    const variedadeId = l.variedadeId as string;
    const chave = chaveGrupoRendimento(l.data, l.setorId, variedadeId);
    let g = mapa.get(chave);
    if (!g) {
      g = { data: l.data, mes: mesDe(l.data), setorId: l.setorId, variedadeId, kg: 0, cachos: 0, kgPorCacho: 0 };
      mapa.set(chave, g);
    }
    g.kg += l.kgAlocados;
    g.cachos += l.cachosAlocados;
  }

  const resultado: GrupoRendimento[] = [];
  for (const g of Array.from(mapa.values())) {
    if (g.cachos <= 0) continue; // sem denominador não há grupo a distribuir
    g.kgPorCacho = g.kg / g.cachos;
    resultado.push(g);
  }
  return resultado;
}

/**
 * Células (setor × mês × variedade).
 *
 * ⚠️ Σ kg ÷ Σ cachos, com a divisão NO FIM. Nunca média dos kg/cacho das vendas — ver o
 * cabeçalho deste arquivo e o teste que asserta explicitamente que B2/julho é 20,22 e NÃO
 * 31,48. A asserção negativa é o que reprova o erro de volta.
 *
 * Sai com `indistinto: false`; quem resolve isso é `marcarIndistintas`.
 */
export function agregarCelulas(linhas: LinhaRendimento[]): CelulaRendimento[] {
  const mapa = new Map<string, CelulaRendimento>();
  const diasPorCelula = new Map<string, Set<string>>();
  const vendasPorCelula = new Map<string, Set<string>>();

  for (const l of linhas) {
    if (!contribui(l)) continue;
    const variedadeId = l.variedadeId as string;
    const mes = mesDe(l.data);
    const chave = chaveCelula(l.setorId, mes, variedadeId);

    let c = mapa.get(chave);
    if (!c) {
      c = {
        setorId: l.setorId,
        setorNome: l.setorNome,
        mes,
        variedadeId,
        variedadeNome: l.variedadeNome ?? "—",
        kg: 0,
        cachos: 0,
        kgPorCacho: null,
        dias: 0,
        vendas: 0,
        indistinto: false,
        amostraPequena: false,
      };
      mapa.set(chave, c);
      diasPorCelula.set(chave, new Set());
      vendasPorCelula.set(chave, new Set());
    }
    c.kg += l.kgAlocados;
    c.cachos += l.cachosAlocados;
    (diasPorCelula.get(chave) as Set<string>).add(l.data);
    (vendasPorCelula.get(chave) as Set<string>).add(l.vendaId);
  }

  const resultado: CelulaRendimento[] = [];
  for (const [chave, c] of Array.from(mapa.entries())) {
    c.dias = (diasPorCelula.get(chave) as Set<string>).size;
    c.vendas = (vendasPorCelula.get(chave) as Set<string>).size;
    // Divisão por zero devolve `null`, nunca Infinity — a tela mostra "—", que é a resposta
    // honesta para "não há cacho alocado aqui".
    c.kgPorCacho = c.cachos > 0 ? c.kg / c.cachos : null;
    c.amostraPequena = c.cachos < CACHOS_AMOSTRA_PEQUENA;
    resultado.push(c);
  }
  return resultado.sort(
    (a, b) => a.setorNome.localeCompare(b.setorNome) || a.mes.localeCompare(b.mes),
  );
}

/**
 * Marca as células que o modelo NÃO consegue distinguir — a limitação que a tela precisa
 * dizer em voz alta.
 *
 * Dentro de UMA venda, todos os grupos da mesma variedade recebem o MESMO kg/cacho por
 * construção do rateio (`kgPorCacho = K(s,v) / Σ aloc(s,·)`, idêntico em todas as linhas
 * daquela variedade). Junho tem uma única venda de Prata (a #9) cobrindo A3, A5 e B3 — e os
 * três aparecem com 17,23 IDÊNTICOS.
 *
 * Isso NÃO é "os três setores rendem igual": é o modelo não conseguindo distingui-los. Sem
 * marcador a tela inventa uma igualdade que ninguém mediu, e alguém decide adubação com base
 * nela. O setor só ganha identidade quando vendas diferentes, com cestas diferentes, o tocam
 * — foi o que aconteceu em julho, com 20 vendas: A4 15,92 e B2 20,22 no mesmo mês.
 *
 * Regra: `indistinto = (vendas === 1) && (essa venda cobre mais de um setor no período)`.
 *
 * A contagem de setores é feita POR (venda, variedade), não por venda: uma venda que toca um
 * setor de Prata e um de Nanica produz kg/cacho DIFERENTE em cada um (K é por variedade),
 * então ela distingue — marcar as duas células seria mentir na direção oposta.
 */
export function marcarIndistintas(
  celulas: CelulaRendimento[],
  linhas: LinhaRendimento[],
): CelulaRendimento[] {
  // (venda, variedade) → setores distintos que ela toca no período carregado.
  const setoresPorVendaVariedade = new Map<string, Set<string>>();
  for (const l of linhas) {
    if (!contribui(l)) continue;
    const chave = `${l.vendaId}|${l.variedadeId}`;
    let setores = setoresPorVendaVariedade.get(chave);
    if (!setores) {
      setores = new Set();
      setoresPorVendaVariedade.set(chave, setores);
    }
    setores.add(l.setorId);
  }

  // Célula → a única venda que a compõe (quando há exatamente uma).
  const vendaUnicaPorCelula = new Map<string, string | null>();
  for (const l of linhas) {
    if (!contribui(l)) continue;
    const chave = chaveCelula(l.setorId, mesDe(l.data), l.variedadeId as string);
    if (!vendaUnicaPorCelula.has(chave)) vendaUnicaPorCelula.set(chave, l.vendaId);
    else if (vendaUnicaPorCelula.get(chave) !== l.vendaId) vendaUnicaPorCelula.set(chave, null);
  }

  return celulas.map(c => {
    if (c.vendas !== 1) return { ...c, indistinto: false };
    const vendaId = vendaUnicaPorCelula.get(chaveCelula(c.setorId, c.mes, c.variedadeId));
    if (!vendaId) return { ...c, indistinto: false };
    const setores = setoresPorVendaVariedade.get(`${vendaId}|${c.variedadeId}`);
    return { ...c, indistinto: (setores ? setores.size : 0) > 1 };
  });
}

// ─── Percentis ──────────────────────────────────────────────────────────────────────────

/**
 * Percentil por interpolação linear sobre a amostra ordenada (método R-7, o mesmo do
 * `PERCENTILE.INC` do Excel e o padrão do numpy). Escolhido por ser o que o Hugo reproduz
 * na planilha se quiser conferir o número na mão.
 */
export function percentil(ordenados: number[], p: number): number {
  if (ordenados.length === 0) return 0;
  if (ordenados.length === 1) return ordenados[0];
  const pos = (ordenados.length - 1) * p;
  const baixo = Math.floor(pos);
  const alto = Math.ceil(pos);
  if (baixo === alto) return ordenados[baixo];
  return ordenados[baixo] + (ordenados[alto] - ordenados[baixo]) * (pos - baixo);
}

/**
 * Distribuição do kg/cacho por variedade, sobre GRUPOS — nunca sobre células nem sobre
 * vendas.
 *
 * ⚠️ Células INDISTINTAS ficam de fora: um valor que é média de outros três não é observação
 * independente, e contá-lo três vezes empurraria os percentis na direção do mês mais pobre em
 * vendas. Os 3 grupos de junho (A3, A5, B3 — todos da venda #9) saem, e o p50 da Prata sai de
 * 62 grupos, não de 65.
 */
export function percentisPorVariedade(
  grupos: GrupoRendimento[],
  celulas: CelulaRendimento[],
  nomePorVariedade?: Map<string, string>,
): DistribuicaoVariedade[] {
  const indistintas = new Set<string>();
  for (const c of celulas) {
    if (c.indistinto) indistintas.add(chaveCelula(c.setorId, c.mes, c.variedadeId));
  }

  const nomes = nomePorVariedade ?? new Map<string, string>();
  if (!nomePorVariedade) {
    for (const c of celulas) nomes.set(c.variedadeId, c.variedadeNome);
  }

  const amostraPorVariedade = new Map<string, number[]>();
  for (const g of grupos) {
    if (indistintas.has(chaveCelula(g.setorId, g.mes, g.variedadeId))) continue;
    const lista = amostraPorVariedade.get(g.variedadeId);
    if (lista) lista.push(g.kgPorCacho);
    else amostraPorVariedade.set(g.variedadeId, [g.kgPorCacho]);
  }

  const resultado: DistribuicaoVariedade[] = [];
  for (const [variedadeId, amostra] of Array.from(amostraPorVariedade.entries())) {
    const ordenados = amostra.slice().sort((a, b) => a - b);
    resultado.push({
      variedadeId,
      variedadeNome: nomes.get(variedadeId) ?? "—",
      n: ordenados.length,
      min: ordenados[0],
      p10: percentil(ordenados, 0.1),
      p25: percentil(ordenados, 0.25),
      p50: percentil(ordenados, 0.5),
      p75: percentil(ordenados, 0.75),
      p90: percentil(ordenados, 0.9),
      max: ordenados[ordenados.length - 1],
      aprendida: ordenados.length >= N_MINIMO_BANDA,
    });
  }
  return resultado.sort((a, b) => b.n - a.n);
}

// ─── A banda de implausibilidade ────────────────────────────────────────────────────────

/**
 * Quanto a banda se afasta do p10/p90, em múltiplos do próprio vão p10–p90.
 *
 * Ver `bandaDaVariedade` para a medição que escolheu este 0,5.
 */
export const MARGEM_BANDA = 0.5;

/**
 * A banda que decide se um kg/cacho é implausível, aprendida da própria variedade.
 *
 * ── POR QUE UMA RÉGUA SÓ NÃO SERVE PARA AS DUAS VARIEDADES ─────────────────────────────
 * Medido na produção sobre os 81 grupos: a MEDIANA da Nanica (30,12) é maior que o MÁXIMO
 * da Prata (28,35) — as duas distribuições quase não se sobrepõem.
 *   • Na Prata a faixa 5–40 NUNCA dispara: a amostra inteira vive entre 13,18 e 28,35. Um
 *     grupo a 28,35, o dobro do p10, passa em silêncio.
 *   • Na Nanica o máximo observado (41,37) dispararia o alarme dos 40 sendo 1,4× a mediana,
 *     que é Nanica saudável.
 *
 * ── POR QUE p10/p90 COM MARGEM, E NÃO AS CERCAS DE TUKEY ──────────────────────────────
 * Tukey (p25 ± 1,5·IQR) é a escolha de manual, e foi a primeira tentativa. MEDIDA contra a
 * produção, ela reprova: na Prata o IQR dos grupos é de só 1,63 (p25 16,86, p75 18,49), a
 * cerca fica em [14,42 · 20,94] e DISPARA EM 7 DAS 19 VENDAS de Prata — incluindo 13,18,
 * 14,16 e 21,66, que são rendimento normal. Alarme que dispara em 37% dos casos é ruído, e
 * ruído é ignorado: o pior estado possível para um aviso.
 *
 * O IQR está artificialmente estreito, e a causa é conhecida: os valores de grupo NÃO são
 * independentes. Dentro de uma venda todos os grupos recebem o mesmo kg/cacho, então a
 * amostra tem massa duplicada no centro (13,18 aparece em 4 grupos de 11/08; 28,35 em 3
 * grupos de 07/08). O selo `indistinto` tira parte dessas cópias, não todas — o resto é o
 * "rateio por rendimento comum", que segue em aberto e é fora do escopo desta fase.
 *
 * p10–p90 atravessa 80% da amostra e é bem menos sensível a essa concentração. Com margem de
 * meio vão para cada lado, a Prata fica em ≈[9,8 · 27,1], que na produção dispara em 4 das 19
 * vendas: as três de ~46 kg/cacho (impossíveis — colheita associada faltando) e a de 28,35,
 * exatamente o caso que a fase existe para deixar de engolir. Margem de um vão inteiro
 * ([5,5 · 31,5]) volta a perder o 28,35 e não acrescenta nada sobre a faixa fixa.
 *
 * É uma ESCOLHA calibrada, não uma verdade. Os percentis crus seguem no payload para a tela
 * desenhar a faixa de referência e para esta conta poder ser refeita.
 *
 * `aprendida: false` (n < N_MINIMO_BANDA, ou vão degenerado) devolve a banda FIXA e diz que
 * é ela. A Nanica, com 19 grupos, cai aqui já no primeiro deploy — que é a melhor forma de o
 * fallback não estar quebrado no dia em que precisar.
 */
export function bandaDaVariedade(dist: DistribuicaoVariedade | undefined): Banda {
  if (!dist || !dist.aprendida) return { ...BANDA_FIXA, aprendida: false };
  const vao = dist.p90 - dist.p10;
  if (!(vao > 0)) return { ...BANDA_FIXA, aprendida: false };
  return {
    // Limite negativo significa "nunca dispara por baixo"; o clamp em 0 é só sanidade de tela.
    min: Math.max(0, dist.p10 - MARGEM_BANDA * vao),
    max: dist.p90 + MARGEM_BANDA * vao,
    aprendida: true,
  };
}

/** Índice por variedade, pronto para `avisoPesoImplausivel` consumir. */
export function bandasPorVariedade(distribuicao: DistribuicaoVariedade[]): Map<string, Banda> {
  const mapa = new Map<string, Banda>();
  for (const d of distribuicao) mapa.set(d.variedadeId, bandaDaVariedade(d));
  return mapa;
}

/** Sem variedade, ou variedade sem histórico: banda fixa, declarada como fixa. */
export function bandaOuFixa(bandas: Map<string, Banda>, variedadeId: string | null): Banda {
  const banda = variedadeId ? bandas.get(variedadeId) : undefined;
  return banda ?? { ...BANDA_FIXA, aprendida: false };
}

export function pesoImplausivel(kgPorCacho: number | null, banda: Banda): boolean {
  if (kgPorCacho === null) return false;
  return kgPorCacho > banda.max || kgPorCacho < banda.min;
}

// ─── Mês parcial ────────────────────────────────────────────────────────────────────────

/**
 * O mês que contém `hoje` está PELA METADE e a tela precisa dizer.
 *
 * A última colheita registrada é 14/08 — agosto tem 14 de 31 dias. Comparar um julho fechado
 * com meio agosto por setor é a mesma armadilha de cobertura do card Colhido: o número não
 * está errado, a base é que é outra. Por isso o mês corrente vem marcado e o Δ contra ele é
 * DECLARADO como parcial, não escondido.
 *
 * ⚠️ `hoje` é sempre data civil da fazenda (`hojeNaFazenda()` no client, `dataHojeNaTz()` no
 * servidor) — nunca `new Date().toISOString()`, que das 21:00 às 23:59 na Bahia já virou o
 * dia seguinte em UTC (#148).
 */
export function mesParcial(mes: string, hoje: string): boolean {
  return mes === mesDe(hoje);
}

export function mesesParciais(meses: string[], hoje: string): string[] {
  return meses.filter(m => mesParcial(m, hoje));
}

/** Os meses presentes nas células, em ordem cronológica. */
export function mesesDasCelulas(celulas: CelulaRendimento[]): string[] {
  const vistos = new Set<string>();
  for (const c of celulas) vistos.add(c.mes);
  return Array.from(vistos).sort();
}
